// "Data" — the clean table under Integrations on Practice Details (David, 2026-10-25:
// "a clean, easy table right where we have Integrations"). One row per data stream
// that applies to this practice: where it comes from, when it last arrived, and
// whether it is live. Same payload as SourceCoverageTable (GET source-coverage), so
// the two never disagree; streams that do not apply here (n/a) are left out.
//
// Everything a person DOES with data — the report uploads, the PM tie-out, the
// scheduled pulls — sits behind one "Uploads & feeds" button, which opens the Data
// page's working cards in a modal (DataPage `embedded`).

import React, { useEffect, useState } from "react";

import "./DataFeedsTable.css";
import DataPage from "./DataPage";
import { StatusBadge, fetchCoverage, freshnessText } from "./SourceCoverageTable";

// "Last email": the newest emailed report (docs/report-inbox.md) ingested for this
// stream, from the same payload; "—" when none has arrived.
export function lastEmailText(item) {
  if (!item || !item.last_email_received_at) return "—";
  const d = new Date(item.last_email_received_at);
  return Number.isNaN(d.getTime()) ? item.last_email_received_at : d.toLocaleString();
}

export default function DataFeedsTable({ practiceId, selectedPractice }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showUploads, setShowUploads] = useState(false);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    fetchCoverage(practiceId)
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  if (!practiceId) return null;

  const labels = (data && data.labels) || {};
  const ingressLabel = (key) => (labels.ingress && labels.ingress[key]) || key;
  const rows = data ? data.items.filter((item) => item.status !== "not_applicable") : [];
  const needsAttention = rows.filter((item) => item.status !== "live").length;

  return (
    <div className="integrations-section data-feeds" data-testid="data-feeds">
      <div className="data-feeds-head">
        <h3>Data</h3>
        {data && (
          <span className="data-feeds-summary">
            {needsAttention === 0
              ? "Everything is up to date"
              : `${needsAttention} ${needsAttention === 1 ? "stream needs" : "streams need"} attention`}
          </span>
        )}
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowUploads(true)}>
          Uploads &amp; feeds
        </button>
      </div>
      {error && (
        <div className="field-message error">
          {error}
          <button className="message-close-sm" aria-label="Dismiss" onClick={() => setError(null)}>
            &times;
          </button>
        </div>
      )}
      {!data && !error && <p className="text-muted">Loading…</p>}
      {data && rows.length === 0 && <p className="text-muted">Nothing is connected for this practice yet.</p>}
      {rows.length > 0 && (
        <table className="detail-table integrations-table data-feeds-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Comes from</th>
              <th>Last received</th>
              <th>Last email</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.key}>
                <td className="int-type-label">{item.label}</td>
                <td>
                  {item.source_label}
                  {item.ingress && item.ingress !== "none" && (
                    <span className="data-feeds-ingress"> · {ingressLabel(item.ingress)}</span>
                  )}
                </td>
                <td title={item.last_updated_at || ""}>{freshnessText(item)}</td>
                <td title={item.last_email_received_at || ""}>{lastEmailText(item)}</td>
                <td title={item.note || ""}>
                  <StatusBadge status={item.status} labels={labels.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showUploads && (
        <div className="modal-overlay" onClick={() => setShowUploads(false)}>
          <div className="create-modal data-feeds-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-feeds-modal-head">
              <h2>Uploads &amp; feeds</h2>
              <button type="button" className="message-close-sm" aria-label="Close" onClick={() => setShowUploads(false)}>
                &times;
              </button>
            </div>
            <DataPage selectedPractice={selectedPractice} embedded />
          </div>
        </div>
      )}
    </div>
  );
}
